import { oc } from "@orpc/contract";
import { z } from "zod";
import { BillingAccountSnapshotSchema, BillingCatalogSchema, BillingSelectionStatusSchema, OfferSelectionRequestSchema, OfferSelectionResponseSchema, PortalSessionResponseSchema, } from "../billing.js";
import { LocaleSchema } from "../sites.js";
// Billing: the plan catalog from the code constants (unit 1.3) and V2's subscription
// commerce (unit 1.6) — checkout, upgrades/downgrades, cancel-at-period-end, the customer
// portal — over the Stripe projection in migration 004. Every command needs a session;
// `selectOffer` also REQUIRES the `Idempotency-Key` header equal to its requestId.
export const billing = {
    /** The site's plans and offers for the pricing page and the splash teaser. Public. */
    catalog: oc
        .route({
        summary: "The site's plans and offers",
        description: "The pricing catalog built from the plan constants, localized; answers signed out and is cached for five minutes.",
    })
        .meta({ public: true, access: "read" })
        .input(z.object({ locale: LocaleSchema.optional() }))
        .output(BillingCatalogSchema),
    /** Everything the billing surfaces render: plan, access state, period, pending change. */
    account: oc
        .route({
        summary: "The viewer's billing snapshot",
        description: "Plan, access state, current period, pending change and the transitions available right now; without Stripe the free snapshot still serves. Needs a session.",
    })
        .meta({ access: "read" })
        .input(z.object({ locale: LocaleSchema.optional() }))
        .output(BillingAccountSnapshotSchema),
    /** Where one selection stands: the checkout or portal return reads it by its selectionId. */
    selection: oc
        .route({
        summary: "Where one selection stands",
        description: "The status of the viewer's own selection named by selectionId (the ?selection= a checkout or portal return carries): pending or processing while it converges, then applied, failed, canceled, expired or superseded. A read: it never repeats or advances the selection. Another account's or site's id, or one that names nothing, is 404 selection_not_found. Needs a session.",
    })
        .meta({ access: "read" })
        .input(z.object({ selectionId: z.uuid() }))
        .output(BillingSelectionStatusSchema),
    /** Pick an offer: checkout, portal deep link, a scheduled change, or "unchanged". */
    selectOffer: oc
        .route({
        summary: "Pick an offer",
        description: "Starts the durable selection named by requestId: a Checkout or portal redirect, a scheduled change, processing, or unchanged. Needs a session and the Idempotency-Key header equal to requestId; a retry with it replays the first answer.",
    })
        .meta({ idempotencyKey: true, access: "write", keys: false })
        .input(OfferSelectionRequestSchema)
        .output(OfferSelectionResponseSchema),
    /** Withdraw a scheduled change (a downgrade or cadence change) before it applies. */
    cancelChange: oc
        .route({
        summary: "Withdraw a scheduled change",
        description: "Cancels a scheduled downgrade or cadence change before it takes effect at the period end. Needs a session.",
    })
        .meta({ access: "write", keys: false })
        .input(z.object({ changeId: z.uuid() }))
        .output(z.object({ canceled: z.literal(true) })),
    /** The Stripe customer portal (payment method, invoices, cancel at period end). */
    portalSession: oc
        .route({
        summary: "Open the Stripe customer portal",
        description: "A short-lived portal URL for the payment method, invoices and cancel-at-period-end; only once a customer exists. Needs a session.",
    })
        .meta({ access: "write", keys: false })
        .input(z.object({}))
        .output(PortalSessionResponseSchema),
};
//# sourceMappingURL=billing.js.map